import { useDispatch } from 'react-redux';
import { Trans } from 'react-i18next';
import { Typography } from '@components/Typography';
import { Input } from '@components/Input';
import { ExternalLink } from '@components/Link';
import { alertActions, accountActions } from '@actions';
import { appleIntegrationService } from '@services';
import { useTranslation, useCopyToClipboard, useThrottle } from '@hooks';
import { handleApiError } from '@helpers/api';
import { ExternalRoutes } from '@constants/routes';
import { CopyIcon } from 'assets/images/icons';
import { Modal, Content } from './styles';

// INFO: CompanyId is the id of the company in the apple developer account
const CompanyId = '1503773198512136192';

const AppleModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { onCopy } = useCopyToClipboard();

    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    const onConnectApple = async () => {
        try {
            await appleIntegrationService.connectIntegration();
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            handleClose={onClose}
            title={t('SettingsIntegrations.appleTitle')}
            subtitle={
                <Typography variant="caption" color="textSecondary">
                    {t('SettingsIntegrations.appleSubtitle')}
                </Typography>
            }
            onPrimaryAction={onConnectApple}
            primaryActionText={t('buttons.connect')}
            primaryActionDisabled={false}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <Content>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '8px' }}>
                    <Trans t={t} i18nKey="SettingsIntegrations.appleDescription1">
                        <ExternalLink
                            href={ExternalRoutes.AppleBusinessConnectLink}
                            variant="caption"
                            underline="hover"
                        >
                            {{ link: 'Apple Business Connect' }}
                        </ExternalLink>
                    </Trans>
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '8px' }}>
                    <Trans t={t} i18nKey="SettingsIntegrations.appleDescription2">
                        <ExternalLink
                            href={ExternalRoutes.AppleBusinessConnectInstructions}
                            variant="caption"
                            underline="hover"
                        >
                            {{ link: t('SettingsIntegrations.theseInstructions') }}
                        </ExternalLink>
                    </Trans>
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ marginBottom: '8px' }}>
                    {t('SettingsIntegrations.appleDescription3')}
                </Typography>
                <Input
                    inputProps={{ value: CompanyId }}
                    onAddonClick={() => onCopyClick(CompanyId)}
                    addonComponent={<CopyIcon style={{ fill: 'rgba(0, 0, 0, 0.6)' }} />}
                    label={t('SettingsIntegrations.partnerCompanyId')}
                    disabled
                    sx={{ width: '50%' }}
                />
                <Typography variant="caption">{t('SettingsIntegrations.appleDescription4')}</Typography>
            </Content>
        </Modal>
    );
};

export default AppleModal;
